import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import type { Invoice } from "~/shared/schemas/invoice-schema";

import { InvoiceCardMobile } from "../invoice-card-mobile/invoice-card-mobile.tsx";
import styles from "./payment-grid-mobile.module.css";

export type PaymentGridMobileProps = Readonly<{
	invoices: Invoice[];
	isLoading: boolean;
	selectRecord: Record<string | number, boolean>;
	onSelect: (id: string | number) => void;
	onDeselect: (id: string | number) => void;
	onDocumentClick: (invoice: Invoice) => void;
	isAllSelected: boolean;
	onSelectAll: (selected: boolean) => void;
}>;

// Invoice cards are much taller than a typical list row (7 fields + header/footer),
// so a smaller batch size keeps the initial render light.
const PAGE_SIZE = 10;
const SCROLL_THRESHOLD = 2;

export const PaymentGridMobile = memo<PaymentGridMobileProps>(
	({
		invoices,
		isLoading,
		selectRecord,
		onSelect,
		onDeselect,
		onDocumentClick,
		isAllSelected,
		onSelectAll,
	}: PaymentGridMobileProps) => {
		const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

		// A new invoice list (e.g. from a status/filter change) restarts the reveal window.
		useEffect(() => {
			setVisibleCount(PAGE_SIZE);
		}, [invoices]);

		const visibleInvoices = useMemo(
			() => invoices.slice(0, Math.min(visibleCount, invoices.length)),
			[invoices, visibleCount],
		);

		// This page scrolls via the app shell's #right-section container, not a
		// local scrollbox (see payment/index.css), so the "load more" listener
		// has to attach there instead of an onScroll prop on our own div.
		useEffect(() => {
			const scrollContainer = document.querySelector("#right-section");
			if (!scrollContainer) return;

			const handleScroll = () => {
				if (visibleCount >= invoices.length) return;
				const distanceFromBottom =
					scrollContainer.scrollHeight -
					(scrollContainer.scrollTop + scrollContainer.clientHeight);
				if (distanceFromBottom <= SCROLL_THRESHOLD) {
					setVisibleCount((count) =>
						Math.min(count + PAGE_SIZE, invoices.length),
					);
				}
			};

			scrollContainer.addEventListener("scroll", handleScroll);
			return () => scrollContainer.removeEventListener("scroll", handleScroll);
		}, [invoices.length, visibleCount]);

		const handleSelectAllChange = useCallback(
			(event: { value: boolean }) => {
				onSelectAll(event.value);
			},
			[onSelectAll],
		);

		if (isLoading) {
			return <LoadingSpinner />;
		}

		return (
			<div
				className={styles.paymentGridMobile}
				data-testid={"invoice-card-list"}
			>
				{invoices.length > 0 && (
					<div className={styles.selectAllRow}>
						<Checkbox
							aria-label={"Select All"}
							checked={isAllSelected}
							onChange={handleSelectAllChange}
						/>
						<span>Select All</span>
					</div>
				)}

				{invoices.length === 0 ? (
					<div className={styles.noRecords}>No records available</div>
				) : (
					visibleInvoices.map((invoice) => (
						<InvoiceCardMobile
							key={invoice.invoiceID}
							invoice={invoice}
							isSelected={selectRecord[invoice.invoiceID] === true}
							onDeselect={onDeselect}
							onDocumentClick={onDocumentClick}
							onSelect={onSelect}
						/>
					))
				)}
			</div>
		);
	},
);

PaymentGridMobile.displayName = "PaymentGridMobile";
